import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

function homePath(role) {
  if (role === 'admin') return '/admin/dashboard';
  if (role === 'office_manager') return '/manager/dashboard';
  if (role === 'client') return '/client-portal';
  return '/officer/dashboard';
}

/**
 * Wraps routes that require authentication.
 * role: single required role string, e.g. "admin"
 * Office managers are allowed through admin routes automatically.
 */
export default function ProtectedRoute({ children, role }) {
  const { user, profile, loading, profileError } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner fullPage />;
  if (!user) return <Navigate to="/login" replace />;

  // Hold until profile is confirmed loaded — prevents nav flash on login
  if (user && !profile && !profileError) return <LoadingSpinner fullPage />;

  // Office managers can access admin-scoped routes (individual pages gate actions via usePermissions)
  const effectiveRole = profile?.role;
  const managerOnAdminRoute = effectiveRole === 'office_manager' && role === 'admin';

  if (role && profile && effectiveRole !== role && !managerOnAdminRoute) {
    return <Navigate to={homePath(effectiveRole)} replace />;
  }

  // Officer must complete onboarding first — but skip if admin override-approved them
  // (status === 'active' with onboarding_complete = false = override-approved officer)
  if (effectiveRole === 'officer' && !profile?.onboarding_complete && profile?.status !== 'active') {
    if (location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Officers who cannot use the app land on the status screen rather than a
  // dashboard that silently renders empty. Every status here fails
  // is_active_officer(), so RLS already returns zero shifts and zero
  // locations — this is the explanation over that boundary, not the boundary.
  // flagged and rejected are legacy and nothing writes them any more, but a
  // historic row still needs routing. Copy for each lives in
  // pages/officer/Pending.js VARIANTS; keep the two lists in sync.
  const BLOCKED_OFFICER_STATUSES = [
    'pending', 'waitlisted', 'paused', 'on_break', 'flagged', 'rejected',
  ];
  if (
    effectiveRole === 'officer' &&
    profile?.onboarding_complete &&
    BLOCKED_OFFICER_STATUSES.includes(profile?.status) &&
    location.pathname !== '/officer/pending'
  ) {
    return <Navigate to="/officer/pending" replace />;
  }

  return children;
}
